// @ts-nocheck
import { Fragment, ReactElement, useEffect, useMemo, useState, VFC } from "react";
import { useTable, useSortBy, TableOptions, Column, useFilters, useGlobalFilter, usePagination, useExpanded, Row } from "react-table";
import { CellRenderer, HeaderRenderer } from ".";
import { FilterRenderers, HeaderRenderers, Renderers, TableSortableColumns } from "./types";
import { _ } from "i18n";
import Loader from "../Loader/Loader";
import Icon from "../Icon/Icon";
import { Form, Field } from "react-final-form";
import { handlePageCount } from "./functions";
import IconArrowLeft from "ui-kit/assets/icon-arrow-left.svg";
import IconArrowRight from "ui-kit/assets/icon-arrow-right.svg";
import "./Datagrid.css";

interface DatagridProps<T extends object> {
	columns: {
		[K in keyof T]: string;
	};
	data: T[];
	cellRenderers?: Renderers<T>;
	headerRenderers?: HeaderRenderers<T>;
	filterRenderers?: FilterRenderers<T>;
	hiddenColumns?: string[];
	className?: string;
	override: boolean;
	headerClassName?: string;
	onRowClick?: (row: T, index: number) => void;
	preselectedRow?: number;
	defaultRenderers?: { Header: HeaderRenderer<T>; Cell: CellRenderer<T> };
	onPaginate?: (pageSize: number, nextPageIndex: number) => void;
	onSort?: (sortBy: { id: string; desc: boolean }[]) => void;
	totalRecords?: number;
	disableSoryBy?: boolean;
	sortableColumns?: string[];
	sortTypes?: { [key: string]: string };
	sortDescFirst?: boolean;
	lengthFilterEnabled?: boolean;
	manualPaginationEnabled?: boolean;
	showLoader?: boolean;
	searchEnabled?: boolean;
	onSearch?: (values: any) => void;
	defaultLength?: number;
	renderRowSubComponent?: (row: any) => JSX.Element;
	pageSize: number;
	rowClickCallback?: (row: Row<T>) => void;
	initCurrentPage: number | undefined;
	descriptionComponent?: ReactElement;
	paginationPerPageOptions?: number[];
}

function makeDefaultRenderers<T extends object>(): { Header: HeaderRenderer<T>; Cell: CellRenderer<T> } {
	return {
		Header: ({ column }, headerName) => {
			return (
				<>
					{headerName}
					<span>
						{column.isSorted ? column.isSortedDesc ? <Icon icon="icon-zoom-arrow-top" /> : <Icon icon="icon-zoom-arrow-bottom" /> : ""}
					</span>
					{/* @ts-ignore */}
					<span className="tooltiptext">{column.tooltip}</span>
				</>
			);
		},
		Cell: ({ cell }) => (cell.value === 0 ? <span className="zero-value">{cell.value}</span> : cell.value),
	};
}

export const Datagrid = <T extends Record<string, any>>({
	columns: columnsDefintion,
	data,
	className,
	override,
	cellRenderers,
	headerRenderers,
	filterRenderers,
	hiddenColumns = [],
	defaultRenderers = makeDefaultRenderers<T>(),
	onPaginate,
	onSort,
	totalRecords,
	disableSoryBy,
	sortableColumns = [],
	sortTypes = {},
	sortDescFirst = false,
	lengthFilterEnabled,
	manualPaginationEnabled,
	showLoader,
	searchEnabled,
	onSearch,
	defaultLength,
	renderRowSubComponent,
	rowClickCallback,
	initCurrentPage,
	descriptionComponent,
	paginationPerPageOptions = [10, 20, 30, 40, 50],
}: DatagridProps<T>): ReturnType<VFC<DatagridProps<T>>> => {
	let columns: TableOptions<T>["columns"];
	const [currentPage, setCurrentPage] = useState<number>(initCurrentPage || 0);
	const [currentPageSize, setCurrentPageSize] = useState<number>(defaultLength ? defaultLength : 18);
	const [currentSortBy, setCurrentSortBy] = useState<any[]>([]);
	const minPageNumber = 1;

	useEffect(() => {
		setCurrentPage(initCurrentPage);
	}, [initCurrentPage]);
	columns = useMemo(
		() =>
			Object.entries(columnsDefintion).map(([accessor, Header]) => {
				return { accessor, Header };
			}),
		[columnsDefintion]
	);

	if (override) {
		columns = useMemo(
			() =>
				Object.entries(columnsDefintion).map(([accessor, headerName]) => {
					return {
						accessor,
						Header: (props) => {
							let renderer = undefined;
							if (headerRenderers) {
								renderer = headerRenderers[accessor];
							}

							return renderer !== undefined ? renderer(props, headerName) : defaultRenderers.Header(props, headerName);
						},

						sortDescFirst: sortDescFirst,
						sortType: sortTypes[accessor] || "alphanumeric",

						Cell: (props: any) => {
							let renderer = undefined;
							if (cellRenderers) {
								renderer = cellRenderers[accessor];
							}

							return renderer !== undefined ? renderer(props) : defaultRenderers.Cell(props);
						},
						tooltip: "_(`game.stats.table.${accessor}`)",
						Filter: () => {
							let renderer = undefined;
							if (filterRenderers) {
								renderer = filterRenderers[accessor];
							}
							return renderer;
						},
						disableSortBy: sortableColumns.includes(TableSortableColumns.SORT_ALL_COLUMNS) ? false : !sortableColumns.includes(accessor),
					} as Column<T>;
				}),
			[columnsDefintion]
		);
	}

	const tableInstance = useTable(
		{
			columns,
			data,
			initialState: {
				hiddenColumns: hiddenColumns,
				pageIndex: currentPage,
				pageSize: currentPageSize,
				sortBy: currentSortBy,
			},
			manualPagination: true,
			manualSortBy: true,
			pageCount: totalRecords && handlePageCount(totalRecords, currentPageSize),
			disableSortBy: disableSoryBy,
			autoResetSortBy: false,
		},
		useFilters,
		useGlobalFilter,
		useSortBy,
		useExpanded,
		usePagination
	);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		footerGroups,
		page,
		prepareRow,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		visibleColumns,
		state: { expanded, pageIndex, pageSize, sortBy },
		setHiddenColumns,
	} = tableInstance;

	const onSearchSubmit = (values: any) => {
		if (onSearch) {
			onSearch(values);
		}
	};

	let prevHiddenColumns: string[] = [];

	// rome-ignore lint/nursery/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (JSON.stringify(prevHiddenColumns) !== JSON.stringify(hiddenColumns)) {
			prevHiddenColumns = hiddenColumns;
			setHiddenColumns(hiddenColumns);
		}
	}, [hiddenColumns]);

	useEffect(() => {
		if (onSort && sortBy && sortBy.length > 0) {
			const sortString = sortBy.map(sort => {
				const direction = sort.desc ? '-' : '+';
				return `${sort.id}${direction}`;
			}).join(',');

			setCurrentPage(0);
			if (onPaginate) {
				onPaginate(currentPageSize, 0);
			}
			onSort(sortString);
			setCurrentSortBy(sortBy);
		}
	}, [sortBy, onSort]);

	return (
		<>
			{(lengthFilterEnabled || searchEnabled) && (
				<div className="datatable-top">
					<div>
						<select
							value={pageSize}
							onChange={(e) => {
								setPageSize(Number(e.target.value));
								setCurrentPageSize(Number(e.target.value));
								if (onPaginate) {
									onPaginate(Number(e.target.value), 0);
								}
							}}
						>
							{paginationPerPageOptions.map((length) => (
								<option key={length} value={length}>
									Show {length}
								</option>
							))}
						</select>
					</div>
					{searchEnabled && (
						<Form
							onSubmit={onSearchSubmit}
							render={({ handleSubmit, submitting }) => (
								<>
									<form onSubmit={handleSubmit}>
										<div className="form-row">
											<Field name="search" type="text">
												{({ input, meta }) => (
													<>
														<input {...input} placeholder={_("label.search")} className="search" />
														{meta.error && meta.touched && <span className="form-error">{meta.error}</span>}
													</>
												)}
											</Field>
										</div>
									</form>
								</>
							)}
						/>
					)}
				</div>
			)}

			<table className={className} {...getTableProps()} style={{ width: "auto" }}>
				<thead>
					{headerGroups.map((headerGroup, headerIndex) => (
						<tr {...headerGroup.getHeaderGroupProps()} key={`header-${headerIndex}`}>
							{headerGroup.headers.map((column, columnIndex) => (
								<th {...column.getHeaderProps(column.getSortByToggleProps())} key={`column-${columnIndex}`}>
									{column.render("Header")}
									{column.canFilter ? column.render("Filter") : null}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.length === 0 && showLoader === false && (
						<tr>
							<td colSpan={columns.length} className="text-center">
								{_("datatables.empty_table")}
							</td>
						</tr>
					)}
					{showLoader ? (
						<tr>
							<td colSpan={visibleColumns.length}>
								<Loader />
							</td>
						</tr>
					) : (
						page.map((row, rowIndex) => {
							prepareRow(row);
							return (
								<Fragment key={`fragment-${rowIndex}`}>
									<tr {...row.getRowProps()} onClick={() => rowClickCallback?.(row)} key={`row-${rowIndex}`}>
										{row.cells.map((cell, cellIndex) => {
											return (
												<td {...cell.getCellProps()} key={`cell-${cellIndex}`}>
													{cell.render("Cell")}
												</td>
											);
										})}
									</tr>
									{row.isExpanded ? (
										<tr key={`row-${rowIndex}`}>
											<td />
											<td colSpan={visibleColumns.length - 1}>{renderRowSubComponent?.({ row })}</td>
										</tr>
									) : null}
								</Fragment>
							);
						})
					)}
				</tbody>
				<tfoot>
					<tr>
						<td colSpan={visibleColumns.length} />
					</tr>
				</tfoot>
			</table>

			{manualPaginationEnabled && (totalRecords ? totalRecords > pageSize : false) ? (
				<div className="datagrid-pagination">
					<div className={"paggination-wrap"}>
						<div className="pagination-left">
							<button
								onClick={() => {
									gotoPage(0);
									setCurrentPage(0);
									if (onPaginate) {
										onPaginate(pageSize, 0);
									}
								}}
								disabled={!canPreviousPage}
							>
								{
									<>
										<img className={"icon-size"} src={IconArrowLeft} />
										<img className={"icon-size"} src={IconArrowLeft} />
									</>
								}
							</button>
							<button
								onClick={() => {
									previousPage();
									const navPageIndex = pageIndex - 1;
									setCurrentPage(navPageIndex);
									if (onPaginate) {
										onPaginate(pageSize, navPageIndex);
									}
								}}
								disabled={!canPreviousPage}
							>
								{<img className={"icon-size"} src={IconArrowLeft} />}
							</button>
							<button
								onClick={() => {
									nextPage();
									const navPageIndex = pageIndex + 1;
									setCurrentPage(navPageIndex);
									if (onPaginate) {
										onPaginate(pageSize, navPageIndex);
									}
								}}
								disabled={!canNextPage}
							>
								{<img className={"icon-size"} src={IconArrowRight} />}
							</button>
							<button
								onClick={() => {
									gotoPage(pageCount - 1);
									const navPageIndex = pageCount - 1;
									setCurrentPage(navPageIndex);
									if (onPaginate) {
										onPaginate(pageSize, navPageIndex);
									}
								}}
								disabled={!canNextPage}
							>
								{
									<>
										<img className={"icon-size"} src={IconArrowRight} />
										<img className={"icon-size"} src={IconArrowRight} />
									</>
								}
							</button>
						</div>

						<span>
							{_("label.pagination.page")} {pageIndex + 1} {_("label.pagination.of")} {pageOptions.length} |{" "}
							{_("label.pagination.go.to.page")}
						</span>
						<input
							type="number"
							value={currentPage + 1}
							min={minPageNumber}
							onKeyDown={(e) => {
								// Added e, E to avoid exponential value. Eg: eˣ
								if (["e", "E", "+", "-", "."].includes(e.key)) {
									e.preventDefault();
								}
							}}
							onChange={(e) => {
								e.preventDefault();

								if (e.target.value > pageOptions.length) {
									setCurrentPage(Number(pageOptions.length - 1));
									if (onPaginate) {
										onPaginate(pageSize, pageOptions.length - 1);
									}
									return;
								}
								const page = e.target.value ? Number(e.target.value) - 1 : 0;
								setCurrentPage(Number(page));
								if (onPaginate) {
									onPaginate(pageSize, page);
								}
							}}
							style={{ width: "100px" }}
						/>
					</div>
					{descriptionComponent}
				</div>
			) : (
				descriptionComponent
			)}
		</>
	);
};

export default Datagrid;
